import { Component, OnInit, inject } from '@angular/core';
import { ParagraphComponent } from '@app/shared/components/text/p/p.component';
import { Modal, ModalType, NonPersistedModalName } from '@app/shared/models/modal.model';
import { HelperService } from '@app/shared/services/helper.service';
import { LoggerService } from '@app/shared/services/logger.service';
import { EventManager } from '@app/shared/utils/event-manager';
import { TranslateService } from '@ngx-translate/core';
import { Streak } from '@app/shared/models/user.model';
import { StreakComponent } from '../streak/streak.component';
import { SharedModule } from '@app/shared/shared.module';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { UserRepository } from '@app/shared/state/user/user.repository';

enum StreakStepStateEnum {
  Empty = 0,
  Completed = 1,
  Warning = 2,
  Locked = 3,
  Unlocked = 4,
  Safe = 5,
}

@Component({
  selector: 'slotcom-streak-detail',
  templateUrl: './streak-detail.component.html',
  styleUrls: ['./streak-detail.component.scss'],
  standalone: true,
  imports: [ParagraphComponent, StreakComponent, SharedModule],
})
export class StreakDetailModalComponent implements OnInit {
  private translateService = inject(TranslateService);
  private helperService = inject(HelperService);
  private eventManager = inject(EventManager);
  logger = inject(LoggerService);
  private userRepo = inject(UserRepository);
  streakDetailData = inject<Modal>(MAT_DIALOG_DATA);

  modalStreakInfo: Record<string, string> = {};
  streakSteps: number[] = [];

  get streak(): Streak {
    return this.userRepo.getStreak();
  }

  ngOnInit(): void {
    this.initializeData();
  }

  openInfo(): void {
    this.eventManager.openModal(
      new Modal(NonPersistedModalName.InfoDailyChallengeComponent, ModalType.View, {
        streak: this.streak,
        dailychallenge: this.streakDetailData.data,
      })
    );
  }

  closeModal() {
    this.eventManager.closeModal(
      new Modal(NonPersistedModalName.StreakDetailModalComponent, ModalType.View, this.streak)
    );
  }

  private initializeData() {
    this.streakSteps = Array.from({ length: this.streak.threshold }, (_, index) => index + 1);

    this.modalStreakInfo = {
      lockIcon: this.helperService.getCloudServiceCdnContent(
        `/images/modals/streak/${this.streak.current < this.streak.threshold ? 'Streak_Locked_Icon' : 'Streak_Unlocked_Icon'}.png`
      ),
      lockedText:
        this.streak.current < this.streak.threshold
          ? this.translateService.instant('home.streak.reward.locked', { x: this.streak.threshold })
          : this.translateService.instant('home.streak.reward.unlocked'),
      rewardText: this.translateService.instant('home.streak.reward-info', { x: this.streak.extraRewardPercentage }),
      stateText: this.getStateText(this.calculateStreakState()),
      emptyStep: this.helperService.getCloudServiceCdnContent(`/images/modals/streak/Streak_CheckBox_Empty.webp`),
      greenStep: this.helperService.getCloudServiceCdnContent(`/images/modals/streak/Streak_CheckBox_Green.webp`),
      yellowStep: this.helperService.getCloudServiceCdnContent(`/images/modals/streak/Streak_CheckBox_Yellow.webp`),
    };
  }

  private calculateStreakState(): StreakStepStateEnum {
    if (this.streak.current === this.streak.threshold) {
      if (this.streak.achievedToday) {
        return StreakStepStateEnum.Safe;
      } else {
        return StreakStepStateEnum.Warning;
      }
    } else {
      if (this.streak.achievedToday) {
        if (this.streak.current < this.streak.threshold) {
          return StreakStepStateEnum.Completed;
        } else {
          return StreakStepStateEnum.Unlocked;
        }
      } else {
        if (this.streak.current === 0 || (this.streak.current > 0 && this.streak.current < this.streak.threshold)) {
          return StreakStepStateEnum.Locked;
        } else {
          return StreakStepStateEnum.Warning;
        }
      }
    }
  }

  private getStateText(state: StreakStepStateEnum): string {
    const stateKeys = {
      [StreakStepStateEnum.Completed]: 'home.streak.state.locked-challenge-completed',
      [StreakStepStateEnum.Warning]: 'home.streak.state.unlocked-danger',
      [StreakStepStateEnum.Locked]: 'home.streak.state.locked2',
      [StreakStepStateEnum.Unlocked]: 'home.streak.state.unlocked2',
      [StreakStepStateEnum.Safe]: 'home.streak.state.unlocked-safe',
    };

    return stateKeys[state];
  }
}
